import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiShare2, FiBookmark, FiClock, FiEye, FiMessageCircle, FiHeart, FiDownload, FiFlag, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { MdVerified } from 'react-icons/md';
import { postsAPI, commentsAPI, reactionsAPI, favoritesAPI } from '../../services/api';
import { API_URL, CATEGORIES, REACTIONS } from '../../utils/constants';
import { useAuth } from '../../context/AuthContext';
import CommentSection from '../../components/CommentSection/CommentSection';
import ReportModal from '../../components/ReportModal/ReportModal';
import { resolveMediaUrl } from '../../utils/media';
import toast from 'react-hot-toast';
import './ArticlePage.css';

const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('abstract');
  const [reactions, setReactions] = useState({});
  const [userReaction, setUserReaction] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const loadArticle = useCallback(async () => {
    try {
      const response = await postsAPI.getById(slug);
      setArticle(response.data);
      
      // Load comments
      const commentsResponse = await commentsAPI.getByPost(response.data.id);
      setComments(commentsResponse.data || []);
      
      // Load reactions + user reaction + favorite
      try {
        const [reactionsResponse, userReactionRes, favRes] = await Promise.all([
          reactionsAPI.getByPost(response.data.id).catch(() => ({ data: {} })),
          isAuthenticated ? reactionsAPI.getUserReaction(response.data.id).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
          isAuthenticated ? favoritesAPI.check(response.data.id).catch(() => ({ data: {} })) : Promise.resolve({ data: {} })
        ]);
        setReactions(reactionsResponse.data?.reactions || {});
        setUserReaction(userReactionRes.data?.userReaction || null);
        setIsSaved(Boolean(favRes?.data?.isFavorite));
      } catch (e) {
        console.log('Reactions/favorites not available');
      }
    } catch (error) {
      console.error('Error loading article:', error);
    } finally {
      setLoading(false);
    }
  }, [slug, isAuthenticated]);

  useEffect(() => {
    loadArticle();
  }, [loadArticle]);

  const handleAddComment = async (commentData) => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para comentar');
      return;
    }

    try {
      await commentsAPI.create({
        postId: article.id,
        content: commentData.text,
        parentId: commentData.parentId
      });
      loadArticle();
      toast.success('Comentario añadido');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Error al añadir comentario');
    }
  };

  const handleEditComment = async (commentId, data) => {
    try {
      await commentsAPI.update(commentId, data);
      loadArticle();
      toast.success('Comentario editado');
    } catch (error) {
      console.error('Error editing comment:', error);
      toast.error('Error al editar comentario');
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await commentsAPI.delete(commentId);
      loadArticle();
      toast.success('Comentario eliminado');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Error al eliminar comentario');
    }
  };

  const handleReaction = async (reactionType) => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para reaccionar');
      return;
    }
    
    try {
      const res = await reactionsAPI.toggle(article.id, reactionType);
      setUserReaction(res.data?.userReaction || null);
      setReactions(res.data?.reactions || {});
    } catch (error) {
      console.error('Error toggling reaction:', error);
    }
  };

  const handleReportSubmit = async ({ reason, description }) => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para reportar');
      return;
    }
    try {
      await postsAPI.report(article.id, { reason, description });
      toast.success('Reporte enviado');
      setReportOpen(false);
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error('No se pudo enviar el reporte');
    }
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para guardar');
      return;
    }
    try {
      if (isSaved) {
        await favoritesAPI.remove(article.id);
        setIsSaved(false);
        toast.success('Eliminado de guardados');
      } else {
        await favoritesAPI.add(article.id);
        setIsSaved(true);
        toast.success('¡Guardado!');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('No se pudo actualizar guardados');
    }
  };

  const isOwner = Boolean(user?.id && article?.author?.id && user.id === article.author.id);

  const handleEdit = () => {
    navigate(`/posts/edit/${article.id}`);
  };

  const handleDeletePost = async () => {
    if (!window.confirm('¿Eliminar esta publicación? Esta acción no se puede deshacer.')) return;
    try {
      await postsAPI.deletePost(article.id);
      toast.success('Publicación eliminada');
      navigate('/');
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('No se pudo eliminar');
    }
  };

  const handleShare = async () => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para compartir');
      return;
    }
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: article.title, url });
      } catch (e) {}
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Enlace copiado al portapapeles');
    }
  };

  const handleDownloadPDF = () => {
    if (!article?.id) return;
    const url = `${API_URL}/posts/${article.id}/pdf`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const processContent = (content, articleImages, isHtml = true) => {
    if (!content) return '';
    
    let processedContent = content;
    
    // Si no es HTML, convertir saltos de línea en párrafos
    if (!isHtml) {
      processedContent = content
        .split('\n\n')
        .filter(p => p.trim())
        .map(p => `<p>${p.trim()}</p>`)
        .join('');
    }
    
    if (articleImages && articleImages.length > 0) {
      articleImages.forEach((img, index) => {
        const placeholder = `{{IMAGE_${index + 1}}}`;
        const imgHtml = `
          <figure class="article-figure">
            <img src="${resolveMediaUrl(img.file_path)}" alt="${img.caption || `Figura ${index + 1}`}" />
            ${img.caption ? `<figcaption>Figura ${index + 1}: ${img.caption}</figcaption>` : ''}
          </figure>
        `;
        processedContent = processedContent.replace(placeholder, imgHtml);
      });
    }
    
    return processedContent;
  };

  const getCategoryInfo = (categoryId) => {
    return CATEGORIES.find(cat => cat.id === categoryId) || { name: categoryId, color: '#6366f1' };
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="article-page-loading">
        <div className="loading-spinner"></div>
        <p>Cargando artículo...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="article-page-error">
        <h2>Artículo no encontrado</h2>
        <Link to="/" className="back-link">Volver al inicio</Link>
      </div>
    );
  }

  const categoryInfo = getCategoryInfo(article.category);
  
  // Función para parsear secciones desde HTML
  const parseHTMLSections = (htmlContent) => {
    if (!htmlContent) return [];
    
    // Dividir por <h2> tags
    const regex = /<h2>(.*?)<\/h2>([\s\S]*?)(?=<h2>|$)/gi;
    const matches = [];
    let match;
    
    while ((match = regex.exec(htmlContent)) !== null) {
      const title = match[1].trim();
      const content = match[2].trim();
      if (title && content) {
        matches.push({
          id: title.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-'),
          label: title,
          content: content,
          isHtml: true
        });
      }
    }
    return matches;
  };

  // Mapeo de section_type a labels en español
  const sectionLabels = {
    'abstract': 'Resumen',
    'intro': 'Introducción', 
    'section': 'Sección',
    'subsection': 'Subsección',
    'conclusion': 'Conclusiones',
    'bibliography': 'Bibliografía'
  };
  
  // Determinar qué secciones usar - PRIORIDAD: article_sections con figuras
  let sections = [];
  
  // 1. PRIORIDAD: Secciones de article_sections (tienen figuras)
  if (article.sections && article.sections.length > 0) {
    sections = article.sections.map(section => ({
      id: section.section_type || `section-${section.id}`,
      label: section.title || sectionLabels[section.section_type] || section.section_type,
      content: section.content,
      images: section.images?.filter(img => img.id) || []
    }));
  }
  // 2. Campos directos de la DB (abstract, introduction, etc.)
  else if (article.abstract || article.introduction) {
    sections = [
      { id: 'abstract', label: 'Resumen', content: article.abstract, images: [] },
      { id: 'introduction', label: 'Introducción', content: article.introduction, images: [] },
      { id: 'framework', label: 'Marco Teórico', content: article.theoretical_framework, images: [] },
      { id: 'methodology', label: 'Metodología', content: article.methodology, images: [] },
      { id: 'results', label: 'Resultados', content: article.results, images: [] },
      { id: 'discussion', label: 'Discusión', content: article.discussion, images: [] },
      { id: 'conclusions', label: 'Conclusiones', content: article.conclusions, images: [] }
    ].filter(s => s.content);
  }
  // 3. Si hay content con HTML embebido
  else if (article.content && article.content.includes('<h2>')) {
    sections = parseHTMLSections(article.content);
  }

  const hasStructuredSections = sections.length > 0;

  return (
    <div className="article-page">
      <div className="article-page-inner">
        {/* Header */}
        <header className="article-header">
          <Link to="/" className="back-button">
            <FiArrowLeft />
            <span>Volver</span>
          </Link>
          <div className="header-actions">
            <button 
              className={`action-btn ${isSaved ? 'active' : ''}`}
              onClick={handleSave}
              title="Guardar"
            >
              <FiBookmark />
            </button>
            <button className="action-btn" onClick={handleShare} title="Compartir">
              <FiShare2 />
            </button>
          </div>
        </header>

        {/* Article Container */}
        <div className="article-container">
        {/* Sidebar Navigation */}
        <aside className="article-sidebar">
          <nav className="section-nav">
            <h4>Contenido</h4>
            {sections.map(section => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className={activeSection === section.id ? 'active' : ''}
                onClick={() => setActiveSection(section.id)}
              >
                {section.label}
              </a>
            ))}
            {article.bibliography?.length > 0 && (
              <a href="#bibliography" onClick={() => setActiveSection('bibliography')}>
                Bibliografía
              </a>
            )}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="article-main">
          {/* Paper Header */}
          <div className="paper-header">
            <span className="paper-category" style={{ backgroundColor: categoryInfo.color }}>
              {categoryInfo.name}
            </span>
            
            <h1 className="paper-title">{article.title}</h1>
            
            {/* Authors */}
            <div className="paper-authors">
              {article.authors?.map((author, index) => (
                <div key={index} className="author-item">
                  <span className="author-name">{author.name}</span>
                  {author.affiliation && (
                    <span className="author-affiliation">{author.affiliation}</span>
                  )}
                </div>
              )) || (
                <div className="author-item">
                  {article.author?.username || article.author?.id ? (
                    <Link
                      to={`/${article.author?.username || article.author?.id}`}
                      className="author-name"
                      style={{ textDecoration: 'none' }}
                    >
                      {article.author?.name}
                      {article.author?.verification_level && article.author.verification_level !== 'none' && (
                        <MdVerified
                          style={{
                            marginLeft: 6,
                            verticalAlign: 'middle',
                            color: article.author.badge_color || 'currentColor'
                          }}
                          title={article.author.badge_label || 'Cuenta verificada'}
                        />
                      )}
                    </Link>
                  ) : (
                    <span className="author-name">
                      {article.author?.name}
                      {article.author?.verification_level && article.author.verification_level !== 'none' && (
                        <MdVerified
                          style={{
                            marginLeft: 6,
                            verticalAlign: 'middle',
                            color: article.author.badge_color || 'currentColor'
                          }}
                          title={article.author.badge_label || 'Cuenta verificada'}
                        />
                      )}
                    </span>
                  )}
                  {article.author?.affiliation && (
                    <span className="author-affiliation">{article.author.affiliation}</span>
                  )}
                </div>
              )}
            </div>

            {/* Meta Info */}
            <div className="paper-meta">
              <span><FiClock /> {formatDate(article.created_at)}</span>
              <span><FiEye /> {article.views?.toLocaleString() || 0} lecturas</span>
              <span><FiMessageCircle /> {comments.length} comentarios</span>
            </div>

            {/* Keywords */}
            {article.keywords?.length > 0 && (
              <div className="paper-keywords">
                <strong>Palabras clave:</strong>
                {article.keywords.map((keyword, index) => (
                  <span key={index} className="keyword">{keyword}</span>
                ))}
              </div>
            )}

            {/* Reactions - Solo para usuarios logueados */}
            {isAuthenticated ? (
              <div className="scientific-reactions">
                <span className="reactions-label">Reacciones:</span>
                <div className="reactions-buttons">
                  {REACTIONS.map(reaction => (
                    <button
                      key={reaction.id}
                      className={`reaction-btn ${userReaction === reaction.id ? 'active' : ''}`}
                      onClick={() => handleReaction(reaction.id)}
                      title={reaction.label}
                    >
                      <span className="reaction-emoji">{reaction.emoji}</span>
                      <span className="reaction-count">{reactions[reaction.id] || 0}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="login-to-react">
                <span>💡 <Link to="/login">Inicia sesión</Link> para reaccionar a este artículo</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="article-actions">
              <button 
                className={`article-action-btn ${userReaction === 'love' ? 'active' : ''}`}
                onClick={() => handleReaction('love')}
              >
                <FiHeart />
                <span>{reactions?.love || 0}</span>
              </button>
              <button className="article-action-btn" onClick={() => document.querySelector('.article-comments').scrollIntoView({ behavior: 'smooth' })}>
                <FiMessageCircle />
                <span>{comments.length}</span>
              </button>
              <button className="article-action-btn" onClick={handleDownloadPDF}>
                <FiDownload />
                <span>PDF</span>
              </button>
              <button className="article-action-btn" onClick={handleShare}>
                <FiShare2 />
                <span>Compartir</span>
              </button>
              <button 
                className={`article-action-btn ${isSaved ? 'active' : ''}`}
                onClick={handleSave}
              >
                <FiBookmark />
              </button>
              <button
                className="article-action-btn"
                onClick={() => {
                  if (!isAuthenticated) {
                    toast.error('Inicia sesión para reportar');
                    return;
                  }
                  setReportOpen(true);
                }}
              >
                <FiFlag />
              </button>

              {isOwner && (
                <>
                  <button className="article-action-btn" onClick={handleEdit} title="Editar">
                    <FiEdit2 />
                  </button>
                  <button className="article-action-btn" onClick={handleDeletePost} title="Eliminar">
                    <FiTrash2 />
                  </button>
                </>
              )}
            </div>

            <ReportModal
              open={reportOpen}
              onClose={() => setReportOpen(false)}
              onSubmit={handleReportSubmit}
            />
          </div>

          {/* Cover Image */}
          <figure className="paper-cover">
            <img 
              src={resolveMediaUrl(article.image) || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800'} 
              alt={article.title} 
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800';
              }}
            />
          </figure>

          {/* Paper Content */}
          <article className="paper-content">
            <div className="paper-columns">
              {sections.map(section => (
                <section key={section.id} id={section.id} className={`paper-section ${section.id === 'intro' || section.id === 'abstract' ? 'abstract-section' : ''}`}>
                  <h2>{section.label}</h2>
                  <div className="section-content">
                    <p>{section.content}</p>
                  </div>
                  {/* Section figures with captions */}
                  {section.images && section.images.length > 0 && (
                    <div className="section-figures">
                      {section.images.map((img, idx) => (
                        <figure key={idx} className="section-figure">
                          <img src={resolveMediaUrl(img.image_url)} alt={img.alt_text || section.label} />
                          {img.caption && <figcaption>{img.caption}</figcaption>}
                        </figure>
                      ))}
                    </div>
                  )}
                </section>
              ))}

              {/* Full Content (if no structured sections) */}
              {!hasStructuredSections && article.content && (
                <section className="paper-section">
                  <div dangerouslySetInnerHTML={{ 
                    __html: processContent(article.content, article.article_images) 
                  }} />
                </section>
              )}

              {/* Bibliography */}
              {/* Bibliography */}
              {article.bibliography?.length > 0 && (
                <section id="bibliography" className="paper-section bibliography">
                  <h2>Bibliografía</h2>
                  <ol>
                    {article.bibliography.map((ref, index) => (
                      <li key={index}>{ref}</li>
                    ))}
                  </ol>
                </section>
              )}
            </div>
          </article>

          {/* Comments Section */}
          <CommentSection
            postId={article.id}
            comments={comments}
            onAddComment={handleAddComment}
            onEditComment={handleEditComment}
            onDeleteComment={handleDeleteComment}
            onRefresh={loadArticle}
          />
        </main>
        </div>
      </div>
    </div>
  );
};

export default ArticlePage;
